/**
 * Health Matrix Widget
 *
 * Sortable table showing tenant health across multiple metric dimensions.
 * Phase 3.5-B: Multi-Tenant Visibility
 */

import React, { useState } from 'react';
import type {
  TenantHealthRow,
  HealthCell,
  MetricDefinition,
  HealthSummary,
  PaginationInfo,
  TenantHealthFilter,
} from '../../../features/dashboard/types/tenantHealth';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Status icons - consistent R/Y/G for health status
const STATUS_ICONS: Record<string, string> = {
  good: '\u{1F7E2}',     // green circle
  warning: '\u{1F7E1}',  // yellow circle
  critical: '\u{1F534}', // red circle
};

// Overall status icons - same R/Y/G scheme
const OVERALL_ICONS: Record<string, string> = {
  healthy: '\u{1F7E2}',  // green circle (matching STATUS_ICONS.good)
  warning: '\u{1F7E1}',  // yellow circle
  critical: '\u{1F534}', // red circle
};

// Isolation badges - distinct icons with progressive blues/purples (NOT R/Y/G to avoid confusion)
const ISOLATION_BADGES: Record<string, { icon: string; label: string; color: string }> = {
  row: { icon: '\u2261', label: 'Row', color: '#818CF8' },         // ≡ Three lines (rows) - Light purple (least isolation)
  schema: { icon: '\u25A4', label: 'Schema', color: '#6366F1' },   // ▤ Striped square (schema) - Medium purple
  container: { icon: '\u2B22', label: 'Container', color: '#4F46E5' }, // ⬢ Hexagon (container) - Dark purple (most isolation)
};

interface CellTooltipProps {
  cell: HealthCell;
  metricLabel: string;
  positionBelow?: boolean;
}

const CellTooltip: React.FC<CellTooltipProps> = ({ cell, metricLabel, positionBelow }) => (
  <div
    className="health-matrix-tooltip"
    style={{
      position: 'absolute',
      ...(positionBelow ? { top: '100%' } : { bottom: '100%' }),
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'var(--bg-elevated, #1a1a2e)',
      border: '1px solid var(--border-color, #333)',
      borderRadius: 'var(--radius-md, 8px)',
      padding: 'var(--space-3, 12px)',
      fontSize: 'var(--text-xs, 12px)',
      whiteSpace: 'nowrap',
      zIndex: 50,
      color: 'var(--text-primary, #fff)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    }}
  >
    <div style={{ fontWeight: 600, marginBottom: 4 }}>{metricLabel}</div>
    <div>Value: {cell.value}{cell.unit}</div>
    <div>Status: {STATUS_ICONS[cell.status]} {cell.status}</div>
    <div>Threshold: warn {cell.threshold.warning}{cell.unit} / crit {cell.threshold.critical}{cell.unit}</div>
    <div>Trend: {cell.trend === 'up' ? '\u2191' : cell.trend === 'down' ? '\u2193' : '\u2194'} {cell.trend}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface HealthMatrixProps {
  tenants: TenantHealthRow[];
  metrics: MetricDefinition[];
  summary: HealthSummary;
  pagination: PaginationInfo;
  isLoading: boolean;
  error: Error | null;

  // Filter
  filter: TenantHealthFilter;
  onFilterChange: (filter: TenantHealthFilter) => void;

  // Sort
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;

  // Pagination
  onPageChange: (page: number) => void;

  // Interactions
  onTenantClick?: (tenantId: string) => void;
  onExport?: () => void;
}

export const HealthMatrix: React.FC<HealthMatrixProps> = ({
  tenants,
  metrics,
  summary,
  pagination,
  isLoading,
  error,
  filter,
  onFilterChange,
  sortBy,
  sortDirection,
  onSort,
  onPageChange,
  onTenantClick,
  onExport,
}) => {
  const [hoveredCell, setHoveredCell] = useState<{ tenantId: string; metric: string } | null>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filter, search: e.target.value });
  };

  const toggleStatusFilter = (status: string) => {
    const next = filter.status.includes(status)
      ? filter.status.filter((s) => s !== status)
      : [...filter.status, status];
    onFilterChange({ ...filter, status: next });
  };

  const toggleIsolationFilter = (type: string) => {
    const next = filter.isolationType.includes(type)
      ? filter.isolationType.filter((t) => t !== type)
      : [...filter.isolationType, type];
    onFilterChange({ ...filter, isolationType: next });
  };

  const sortArrow = (col: string) => {
    if (sortBy !== col) return '';
    return sortDirection === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--space-4)', color: 'var(--error)' }}>
        Failed to load tenant health: {error.message}
      </div>
    );
  }

  return (
    <div className="card card-elevated" style={{ padding: 'var(--space-4)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--text-lg, 18px)', color: 'var(--text-primary)' }}>
          Multi-Tenant Health Matrix
        </h3>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {onExport && (
            <button className="btn btn-secondary btn-sm" onClick={onExport}>
              Export
            </button>
          )}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search tenants..."
          value={filter.search}
          onChange={handleSearchChange}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-color, #333)',
            background: 'var(--bg-secondary, #1a1a2e)',
            color: 'var(--text-primary)',
            fontSize: 'var(--text-sm)',
            minWidth: 180,
          }}
        />
        {/* Status filter chips */}
        {(['healthy', 'warning', 'critical'] as const).map((s) => (
          <button
            key={s}
            onClick={() => toggleStatusFilter(s)}
            style={{
              padding: '4px 10px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${filter.status.includes(s) ? 'var(--accent-primary)' : 'var(--border-color, #444)'}`,
              background: filter.status.includes(s) ? 'var(--accent-primary-muted)' : 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
              textTransform: 'capitalize',
            }}
          >
            {OVERALL_ICONS[s]} {s}
          </button>
        ))}
        {/* Isolation filter chips */}
        {(['row', 'schema', 'container'] as const).map((t) => (
          <button
            key={t}
            onClick={() => toggleIsolationFilter(t)}
            style={{
              padding: '4px 10px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${filter.isolationType.includes(t) ? ISOLATION_BADGES[t].color : 'var(--border-color, #444)'}`,
              background: filter.isolationType.includes(t) ? `${ISOLATION_BADGES[t].color}22` : 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
            }}
          >
            <span style={{ color: ISOLATION_BADGES[t].color }}>{ISOLATION_BADGES[t].icon}</span> {ISOLATION_BADGES[t].label}
          </button>
        ))}
      </div>

      {/* Summary row */}
      <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
        <span>{OVERALL_ICONS.healthy} {summary.healthy_count} Healthy</span>
        <span>{OVERALL_ICONS.warning} {summary.warning_count} Warning</span>
        <span>{OVERALL_ICONS.critical} {summary.critical_count} Critical</span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-color, #333)' }}>
              <th
                style={{ textAlign: 'left', padding: '8px 12px', cursor: 'pointer', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
                onClick={() => onSort('name')}
              >
                Tenant{sortArrow('name')}
              </th>
              <th style={{ textAlign: 'center', padding: '8px', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => onSort('isolation_type')}>Type{sortArrow('isolation_type')}</th>
              {metrics.map((m) => (
                <th
                  key={m.key}
                  style={{ textAlign: 'center', padding: '8px', cursor: 'pointer', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
                  onClick={() => onSort(m.key)}
                  title={m.description}
                >
                  {m.label}{sortArrow(m.key)}
                </th>
              ))}
              <th
                style={{ textAlign: 'center', padding: '8px', cursor: 'pointer', color: 'var(--text-secondary)' }}
                onClick={() => onSort('status')}
              >
                Status{sortArrow('status')}
              </th>
              <th style={{ textAlign: 'center', padding: '8px', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => onSort('version')}>Ver{sortArrow('version')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && tenants.length === 0 ? (
              <tr>
                <td colSpan={metrics.length + 4} style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-muted)' }}>
                  Loading...
                </td>
              </tr>
            ) : tenants.length === 0 ? (
              <tr>
                <td colSpan={metrics.length + 4} style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-muted)' }}>
                  No tenants found
                </td>
              </tr>
            ) : (
              tenants.map((tenant, rowIndex) => (
                <tr
                  key={tenant.tenant_id}
                  style={{ borderBottom: '1px solid var(--border-color, #222)', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover, rgba(255,255,255,0.03))')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  <td
                    style={{ padding: '8px 12px', cursor: onTenantClick ? 'pointer' : 'default', color: onTenantClick ? 'var(--accent-primary, #6366f1)' : 'var(--text-primary)', fontWeight: 500 }}
                    onClick={() => onTenantClick?.(tenant.tenant_id)}
                    onMouseEnter={(e) => { if (onTenantClick) e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                  >
                    {tenant.tenant_name}
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', textDecoration: 'none' }}>{tenant.tenant_slug}</div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px' }}>
                    <span
                      title={ISOLATION_BADGES[tenant.isolation_type]?.label}
                      style={{ color: ISOLATION_BADGES[tenant.isolation_type]?.color }}
                    >
                      {ISOLATION_BADGES[tenant.isolation_type]?.icon}
                    </span>
                  </td>
                  {metrics.map((m) => {
                    const cell: HealthCell | undefined = tenant.metrics[m.key];
                    const isHovered = hoveredCell?.tenantId === tenant.tenant_id && hoveredCell?.metric === m.key;
                    return (
                      <td
                        key={m.key}
                        style={{ textAlign: 'center', padding: '8px', position: 'relative', cursor: 'default' }}
                        onMouseEnter={() => setHoveredCell({ tenantId: tenant.tenant_id, metric: m.key })}
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        {cell ? STATUS_ICONS[cell.status] : '-'}
                        {cell && isHovered && <CellTooltip cell={cell} metricLabel={m.label} positionBelow={rowIndex < 2} />}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'center', padding: '8px' }}>
                    {OVERALL_ICONS[tenant.overall_status]}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                    {tenant.version}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
          <span>
            Showing {(pagination.page - 1) * pagination.page_size + 1}
            {'\u2013'}
            {Math.min(pagination.page * pagination.page_size, pagination.total_items)} of {pagination.total_items}
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="btn btn-secondary btn-sm"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
            >
              Prev
            </button>
            <button
              className="btn btn-secondary btn-sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => onPageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <span><span style={{ color: ISOLATION_BADGES.row.color }}>{ISOLATION_BADGES.row.icon}</span> Row</span>
        <span><span style={{ color: ISOLATION_BADGES.schema.color }}>{ISOLATION_BADGES.schema.icon}</span> Schema</span>
        <span><span style={{ color: ISOLATION_BADGES.container.color }}>{ISOLATION_BADGES.container.icon}</span> Container</span>
        <span style={{ marginLeft: 'var(--space-4)' }}>{STATUS_ICONS.good} Good</span>
        <span>{STATUS_ICONS.warning} Warning</span>
        <span>{STATUS_ICONS.critical} Critical</span>
      </div>
    </div>
  );
};

export default HealthMatrix;
